import { forwardRef } from 'react';
// next
import NextLink from 'next/link';
// @mui
import { Box, ListItemText, Tooltip, Typography } from '@mui/material';
// hooks
import useLocales from '../../../hooks/useLocales';
import useLayoutContext from '@common/hooks/useLayoutContext';
// type
import { NavItemProps } from '@common/@types/nav';
//
import Iconify from '../../Iconify';
import { ListItemIconStyle, ListItemStyle, ListItemStyleProps, ListItemTextStyle } from './style';
import { isExternalLink } from '..';

// ----------------------------------------------------------------------

// HANDLE SHOW ITEM BY ROLE
const ListItem = forwardRef<HTMLDivElement & HTMLAnchorElement, ListItemStyleProps>(
  (props, ref) => {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { ...other } = props;
    return (
      <ListItemStyle {...other} ref={ref}>
        {props.children}
      </ListItemStyle>
    );
  }
);

export function NavItemRoot({ item, isCollapse, open = false, active, onOpen }: NavItemProps) {
  const { translate } = useLocales();
  const { getTypedGuard } = useLayoutContext();
  const BlacklistedAttributeGuard = getTypedGuard('BlacklistedAttributeGuard');

  const { title, path, icon, info, children, disabled, caption, blacklistFunction } = item;

  const renderContent = (
    <>
      {icon && <ListItemIconStyle>{icon}</ListItemIconStyle>}
      <ListItemTextStyle
        disableTypography
        primary={translate(title)}
        secondary={
          <Tooltip title={translate(caption) || ''} arrow>
            <Typography
              noWrap
              variant="caption"
              component="div"
              sx={{ textTransform: 'initial', color: 'text.secondary' }}
            >
              {translate(caption)}
            </Typography>
          </Tooltip>
        }
        isCollapse={isCollapse}
      />
      {!isCollapse && (
        <>
          {info && info}
          {children && <ArrowIcon open={open} />}
        </>
      )}
    </>
  );

  return (
    <BlacklistedAttributeGuard blacklistFunction={blacklistFunction} path={path}>
      {children ? (
        <ListItem onClick={onOpen} activeRoot={active} disabled={disabled}>
          {renderContent}
        </ListItem>
      ) : isExternalLink(path) ? (
        <NextLink href={path} passHref style={{ textDecoration: 'none' }}>
          <ListItem disabled={disabled}>{renderContent}</ListItem>
        </NextLink>
      ) : (
        <NextLink href={path} passHref style={{ textDecoration: 'none' }}>
          <ListItem activeRoot={active} disabled={disabled}>
            {renderContent}
          </ListItem>
        </NextLink>
      )}
    </BlacklistedAttributeGuard>
  );
}

// ----------------------------------------------------------------------

type NavItemSubProps = Omit<NavItemProps, 'isCollapse'>;

export function NavItemSub({ item, open = false, active = false, onOpen }: NavItemSubProps) {
  const { translate } = useLocales();
  const { getTypedGuard } = useLayoutContext();
  const BlacklistedAttributeGuard = getTypedGuard('BlacklistedAttributeGuard');

  const { title, path, info, children, disabled, caption, blacklistFunction } = item;

  const renderContent = (
    <>
      <DotIcon active={active} />
      <ListItemText
        disableTypography
        primary={translate(title)}
        secondary={
          <Tooltip title={translate(caption) || ''} arrow>
            <Typography
              noWrap
              variant="caption"
              component="div"
              sx={{ textTransform: 'initial', color: 'text.secondary' }}
            >
              {translate(caption)}
            </Typography>
          </Tooltip>
        }
      />
      {info && info}
      {children && <ArrowIcon open={open} />}
    </>
  );

  return (
    <BlacklistedAttributeGuard blacklistFunction={blacklistFunction} path={path}>
      {children ? (
        <ListItem onClick={onOpen} activeSub={active} subItem disabled={disabled}>
          {renderContent}
        </ListItem>
      ) : isExternalLink(path) ? (
        <NextLink href={path} passHref style={{ textDecoration: 'none' }}>
          <ListItem subItem disabled={disabled}>
            {renderContent}
          </ListItem>
        </NextLink>
      ) : (
        <NextLink href={path} passHref style={{ textDecoration: 'none' }}>
          <ListItem activeSub={active} subItem disabled={disabled}>
            {renderContent}
          </ListItem>
        </NextLink>
      )}
    </BlacklistedAttributeGuard>
  );
}

// ----------------------------------------------------------------------

type DotIconProps = {
  active: boolean;
};

export function DotIcon({ active }: DotIconProps) {
  return (
    <ListItemIconStyle>
      <Box
        component="span"
        sx={{
          width: 4,
          height: 4,
          borderRadius: '50%',
          bgcolor: 'text.disabled',
          transition: (theme) =>
            theme.transitions.create('transform', {
              duration: theme.transitions.duration.shorter,
            }),
          ...(active && {
            transform: 'scale(2)',
            bgcolor: 'primary.main',
          }),
        }}
      />
    </ListItemIconStyle>
  );
}

// ----------------------------------------------------------------------

type ArrowIconProps = {
  open: boolean;
};

export function ArrowIcon({ open }: ArrowIconProps) {
  return (
    <Iconify
      icon={open ? 'eva:arrow-ios-downward-fill' : 'eva:arrow-ios-forward-fill'}
      sx={{ width: 16, height: 16, ml: 1 }}
    />
  );
}
